import React, {CSSProperties, type ReactElement} from "react";

import {IGatsbyImageData, GatsbyImage} from "gatsby-plugin-image";

import {css} from "/src/utils/utils";

export interface ImageObject extends Queries.AssetFragmentFragment {
  gatsbyImageData?: IGatsbyImageData;
}

export interface ImageProps {
  image: ImageObject;
  classes?: string;
  imageClasses?: string;
  objectFit?: CSSProperties["objectFit"];
  objectPosition?: CSSProperties["objectPosition"];
}

/**
 *
 * @param {ImageProps} props
 * @param {ImageObject} props.image
 * @param {string} props.classes - CSS class applied to the outer wrapper.
 * @param {string} props.imageClasses - CSS class applied to the <img> element.
 * @param {CSSProperties["objectFit"]} props.objectFit - Resizing behavior for the image within its container.
 * @param {CSSProperties["objectPosition"]} - Position of the image within its container.
 * @return {ReactElement}
 */
const Image = (props: ImageProps): ReactElement => {
  const {
    image,
    classes,
    imageClasses,
    objectFit = "cover",
    objectPosition,
  } = props;

  const alt = (() => {
    if (image?.description) {
      return image.description;
    }

    if (image?.title) {
      return image.title;
    }

    return "flexport-image";
  })();

  if (image?.svg && image?.file?.contentType === "image/svg+xml") {
    const {svg, file} = image;

    if (svg.content !== null) {
      // Inline SVGs
      return (
        <div
          className={css(classes!)}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{__html: svg.content}}
        />
      );
    }

    // SVGs that can/should not be inline
    return <img className={css(classes!)} src={file?.url!} alt={alt} />;
  }

  return (
    <GatsbyImage
      className={css(classes!)}
      imgClassName={css(imageClasses!)}
      image={image?.gatsbyImageData!}
      objectFit={objectFit}
      objectPosition={objectPosition}
      alt={alt}
    />
  );
};

export default Image;
